// Client-side PDF to PNG conversion utility using react-pdf
import { pdfjs } from 'react-pdf';

// PDF.js worker is now configured globally via pdfWorker.js import

export interface PdfConversionOptions {
  pages: 'all' | 'first' | 'specific';
  specificPages?: number[];
  combinePages?: boolean;
  quality?: number;
}

export interface PdfInfo {
  numPages: number;
  title?: string;
}


// Simple PDF info extraction using pdfjs
const getPdfInfoInternal = async (file: File): Promise<{ numPages: number; title?: string }> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({ 
      data: arrayBuffer
    }).promise;
    
    return {
      numPages: pdf.numPages,
      title: file.name
    };
  } catch (error) {
    throw new Error(`Failed to analyze PDF: ${error.message}`);
  }
};

// Validate PDF file before processing
const validatePdfFile = (file: File): void => {
  if (!file) {
    throw new Error('No file provided');
  }
  
  if (file.size === 0) {
    throw new Error('PDF file is empty');
  }
  
  if (file.size > 50 * 1024 * 1024) { // 50MB limit
    throw new Error('PDF file is too large (maximum 50MB supported)');
  }
  
  if (!isPdfFile(file)) {
    throw new Error('File is not a valid PDF');
  }
};

// Get PDF information without converting
export const getPdfInfo = async (file: File): Promise<PdfInfo> => {
  try {
    console.log('getPdfInfo: Starting PDF analysis for file:', file.name);
    console.log('getPdfInfo: File size:', file.size, 'bytes');
    
    // Validate file first
    validatePdfFile(file);
    
    console.log('getPdfInfo: Using PDF.js to analyze PDF...');
    const result = await getPdfInfoInternal(file);
    
    console.log('getPdfInfo: Analysis complete:', result);
    return result;
  } catch (error) {
    console.error('getPdfInfo: Error getting PDF info:', error);
    
    // Provide more helpful error messages
    if (error.message.includes('timeout') || error.message.includes('timed out')) {
      throw new Error('PDF analysis timed out. This may be a large file or complex PDF. Try using text processing instead.');
    } else if (error.message.includes('Invalid PDF') || error.message.includes('not a valid PDF')) {
      throw new Error('Invalid PDF file. Please ensure the file is not corrupted.');
    } else if (error.message.includes('too large')) {
      throw new Error('PDF file is too large. Please use a smaller file or try text processing instead.');
    } else {
      throw new Error(`Failed to read PDF information: ${error.message}`);
    }
  }
};

// Convert PDF to PNG(s) with options
export const convertPdfToPng = async (
  file: File, 
  options: PdfConversionOptions = { pages: 'all', quality: 1.5 }
): Promise<File[]> => {
  try {
    console.log('convertPdfToPng: Starting conversion for', file.name);
    
    // Validate file first
    validatePdfFile(file);
    
    console.log('convertPdfToPng: Using react-pdf...');
    
    // Convert File to ArrayBuffer for react-pdf
    const arrayBuffer = await file.arrayBuffer();
    
    // Load PDF document
    const pdf = await pdfjs.getDocument({
      data: arrayBuffer
    }).promise;
    
    // Determine which pages to convert
    let pageNumbers: number[] = [];
    if (options.pages === 'first') {
      pageNumbers = [1];
    } else if (options.pages === 'specific' && options.specificPages) {
      pageNumbers = options.specificPages;
    } else {
      // 'all' pages
      pageNumbers = Array.from({ length: pdf.numPages }, (_, i) => i + 1);
    }
    
    // Convert each page to PNG
    const pngPages = await Promise.all(
      pageNumbers.map(async (pageNum) => {
        const page = await pdf.getPage(pageNum);
        const scale = options.quality || 1.5;
        const viewport = page.getViewport({ scale });
        
        // Create canvas
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        
        // Render page to canvas
        await page.render({ 
          canvasContext: context, 
          viewport: viewport
        }).promise;
        
        // Convert canvas to blob
        const blob = await new Promise<Blob>((resolve) => {
          canvas.toBlob(resolve, 'image/png', 0.95);
        });
        
        return {
          content: await blob.arrayBuffer(),
          pageNumber: pageNum,
          name: `page_${pageNum}.png`,
          path: '',
          width: canvas.width,
          height: canvas.height
        };
      })
    );
    
    console.log('convertPdfToPng: PDF converted to', pngPages.length, 'PNG pages');
    
    const convertedFiles: File[] = [];
    
    if (options.combinePages && pngPages.length > 1) {
      // Combine all pages into one tall image
      console.log('convertPdfToPng: Combining pages into single image');
      const combinedFile = await combineBuffersIntoSingleImage(pngPages, file.name);
      convertedFiles.push(combinedFile);
    } else {
      // Convert each page to individual File objects
      console.log('convertPdfToPng: Converting pages individually');
      for (let i = 0; i < pngPages.length; i++) {
        const pageData = pngPages[i];
        
        const fileName = pngPages.length > 1 
          ? file.name.replace('.pdf', `_page_${pageData.pageNumber}.png`)
          : file.name.replace('.pdf', '.png');
        
        const pngFile = new File([new Uint8Array(pageData.content)], fileName, {
          type: 'image/png',
          lastModified: Date.now()
        });
        
        convertedFiles.push(pngFile);
      }
    }
    
    console.log('convertPdfToPng: Conversion complete, created', convertedFiles.length, 'file(s)');
    return convertedFiles;
  } catch (error) {
    console.error('convertPdfToPng: Error converting PDF to PNG:', error);
    
    // Provide more helpful error messages
    if (error.message.includes('timeout') || error.message.includes('timed out')) {
      throw new Error('PDF conversion timed out. This may be a large file or complex PDF. Try using text processing instead.');
    } else if (error.message.includes('Invalid PDF') || error.message.includes('not a valid PDF')) {
      throw new Error('Invalid PDF file. Please ensure the file is not corrupted.');
    } else if (error.message.includes('too large')) {
      throw new Error('PDF file is too large. Please use a smaller file or try text processing instead.');
    } else {
      throw new Error(`Failed to convert PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
};

// Combine multiple Buffer pages into a single tall image
const combineBuffersIntoSingleImage = async (
  pngPages: Array<{ content: ArrayBuffer; pageNumber: number; name: string; path: string; width: number; height: number }>,
  originalFileName: string
): Promise<File> => {
  // Load all images
  const images = await Promise.all(
    pngPages.map(page => {
      return new Promise<HTMLImageElement>((resolve, reject) => {
        const blob = new Blob([page.content], { type: 'image/png' });
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(url);
          resolve(img);
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Failed to load page image'));
        };
        img.src = url;
      });
    })
  );
  
  // Calculate combined dimensions
  const maxWidth = Math.max(...images.map(img => img.width));
  const totalHeight = images.reduce((sum, img) => sum + img.height, 0);
  
  // Create combined canvas
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    throw new Error('Could not get canvas context');
  }
  
  canvas.width = maxWidth;
  canvas.height = totalHeight;
  
  // Fill with white background
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, maxWidth, totalHeight);
  
  // Draw all images
  let currentY = 0;
  for (const img of images) {
    const x = (maxWidth - img.width) / 2; // Center the page
    ctx.drawImage(img, x, currentY);
    currentY += img.height;
  }
  
  // Convert to File
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Failed to create combined PNG blob'));
        return;
      }
      
      const fileName = originalFileName.replace('.pdf', '_combined.png');
      
      const pngFile = new File([blob], fileName, {
        type: 'image/png',
        lastModified: Date.now()
      });
      
      resolve(pngFile);
    }, 'image/png', 0.95);
  });
};

export const isPdfFile = (file: File): boolean => {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
};

export const isImageFile = (file: File): boolean => {
  return file.type.startsWith('image/') || 
         ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'].some(ext => 
           file.name.toLowerCase().endsWith(ext));
};